import { defineStore } from 'pinia'
import _ from 'lodash'
import type Unit from '@/types/unit'
import { useProjectStore } from '@/store/project'
import type { UnitPlayerDataInterior } from '@/types/unit'
import { ProjectImagesCategory } from '~/types/image'

interface BuildingUnitStats {
    soldUnits: number | null
    availableUnits: number | null
    totalUnits: number | null
    minimumSize: number | null
    maximumSize: number | null
    minimumPrice: number | null
    maximumPrice: number | null
    minimumRooms: number | null
    maximumRooms: number | null
    minimumFloor: number | null
    maximumFloor: number | null
}

export const useUnitStore = defineStore('unitStore', () => {
    const allUnits = ref<Unit[]>([])
    const units = ref<Unit[]>([])
    const buildingUnits = ref<Unit[]>([])
    const unit = ref<Unit | null>(null)
    const unitPlayerData = ref<any>(null)

    const fetchUnits = async (projectSlug: string): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        units.value = await fetchData(`${PUBLIC_API_URL}/${projectSlug}/slug/unit`)
        allUnits.value = units.value
    }

    const fetchUnitsByBuilding = async (
        projectSlug: string,
        buildingSlug: string
    ): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()
        units.value = await fetchData(
            `${PUBLIC_API_URL}/${projectSlug}/slug/building/${buildingSlug}/slug/unit`
        )
    }

    const fetchUnit = async (
        projectSlug: string,
        buildingSlug: string,
        unitId: string
    ): Promise<void> => {
        const { PUBLIC_API_URL, fetchData } = useAPI()

        unit.value = await fetchData(
            `${PUBLIC_API_URL}/${projectSlug}/slug/building/${buildingSlug}/slug/unit/${unitId}/unitId`
        )

        unitPlayerData.value = unit.value.playerData
    }

    const setCurrentUnit = (unitId: string) => {
        unit.value = units.value.find((item: Unit) => item.unitId === unitId)
    }

    const unitThumbnail = computed(() => {
        const { cdn } = useCDN()

        if (unitPlayerData.value.length) {
            return unitPlayerData[0].thumbnail
        }

        return `${cdn}/placeholders/unit-placeholder.png`
    })

    const getUnitFeaturedImage = computed(() => {
        return (unitId: string) => {
            const projectStore = useProjectStore()

            let match

            const unit = units.value.find((unit) => unit.unitId === unitId)

            if (!unit) {
                return
            }

            if (unit.featuredImage) {
                match = projectStore.getProjectImageById(unit.featuredImage)
            }

            const { cdn } = useCDN()
            if (match) {
                return `${cdn}/content/${projectStore.project.slug}/images/${match.category}/${match.filename}`
            }

            return
        }
    })

    const unitThumbnailByUnitId = (unitId: string) => {
        const { cdn } = useCDN()

        // TODO: correct placeholder url
        let thumbnail = `${cdn}/placeholders/unit-placeholder.png`

        const unit = units.value.find((unit) => unit.unitId === unitId)

        if (!unit) return thumbnail

        const unitFeaturedImage = getUnitFeaturedImage.value(unitId)
        if (unitFeaturedImage) return unitFeaturedImage

        if (unit.playerData) {
            const thumbnail = unit.playerData.find(
                (interior: UnitPlayerDataInterior) => interior.isValid
            )?.thumbnail

            if (thumbnail) return `${cdn}/${thumbnail}`
        }

        return thumbnail
    }

    const unitImages = computed(() => {
        // TODO: unitImages
        return unit.value.images
    })

    const getBuildingUnitsStatistics = computed(() => {
        return (buildingId: number) => {
            const buildingStats: BuildingUnitStats = {
                soldUnits: null,
                availableUnits: null,
                totalUnits: 0,
                minimumSize: null,
                maximumSize: null,
                minimumPrice: null,
                maximumPrice: null,
                minimumRooms: null,
                maximumRooms: null,
                minimumFloor: null,
                maximumFloor: null
            }

            const buildingUnits = allUnits.value.filter((unit) => unit.buildingId === buildingId)
            const projectStore = useProjectStore()

            let availableUnits = buildingUnits
            let buildingSoldUnits = []

            if (projectStore.projectStatuses.length > 0) {
                availableUnits = buildingUnits.filter(
                    (unit) => !projectStore.getStatusByStatusId(unit.status).isSold
                )
                buildingSoldUnits = buildingUnits.filter(
                    (unit) => projectStore.getStatusByStatusId(unit.status).isSold
                )
            }

            if (buildingUnits.length) {
                buildingStats.soldUnits = buildingSoldUnits.length
                buildingStats.totalUnits = buildingUnits.length
                buildingStats.availableUnits = availableUnits.length

                if (!availableUnits) {
                    return buildingStats
                }

                buildingStats.minimumSize = _.minBy(availableUnits, 'size')?.size
                buildingStats.maximumSize = _.maxBy(availableUnits, 'size')?.size

                buildingStats.minimumPrice = _.minBy(availableUnits, 'price')?.price
                buildingStats.maximumPrice = _.maxBy(availableUnits, 'price')?.price

                buildingStats.minimumRooms = _.minBy(availableUnits, 'rooms')?.rooms
                buildingStats.maximumRooms = _.maxBy(availableUnits, 'rooms')?.rooms

                buildingStats.minimumFloor = _.minBy(availableUnits, 'floor')?.floor
                buildingStats.maximumFloor = _.maxBy(availableUnits, 'floor')?.floor
            }

            return buildingStats
        }
    })

    const unitCanBeBought = computed(() => {
        return (unit: Partial<Unit>) => {
            if (!unit.buyUrl || unit.buyUrl === '') return false

            const projectStore = useProjectStore()
            const unitStatus = projectStore.getUnitStatus(unit)

            if (!unitStatus) return false
            if (unitStatus.isSold) return false

            return true
        }
    })

    const getUnitImageFiles = (unitId: string) => {
        const thisUnit = unit.value

        if (!thisUnit) return []
        if (thisUnit.unitId !== unitId) return []

        const thisUnitImages = unitImages.value
        if (!thisUnitImages || thisUnitImages.length < 1) return []

        const projectStore = useProjectStore()
        const imagesArray = projectStore.getProjectImagesInArray(thisUnitImages)

        if (!imagesArray || imagesArray.length < 1) return []

        const { cdn } = useCDN()
        return imagesArray.map((item) => {
            return `${cdn}/content/${projectStore.project.slug}/images/${item.category}/${item.filename}`
        })
    }

    const getBuildingUnitsStatusSummary = computed(() => {
        return (buildingId: number) => {
            const buildingUnits = allUnits.value.filter((unit) => unit.buildingId === buildingId)
            const projectStore = useProjectStore()

            const statusSummary = projectStore.projectStatuses.map((status) => {
                const countOfUnits = buildingUnits.filter(
                    (unit) => unit.status === status.id
                ).length
                return { status: status, count: countOfUnits }
            })

            return _.orderBy(statusSummary, ['count'], ['desc'])
        }
    })

    return {
        units,
        buildingUnits,
        unit,
        unitPlayerData,
        unitImages,
        fetchUnits,
        fetchUnit,
        setCurrentUnit,
        fetchUnitsByBuilding,
        getBuildingUnitsStatistics,
        getBuildingUnitsStatusSummary,
        unitThumbnail,
        unitThumbnailByUnitId,
        getUnitFeaturedImage,
        unitCanBeBought,
        getUnitImageFiles
    }
})
